import { useCallback } from "react";
import { useNavigate } from "react-router";

import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import { HUMANIZE_PROVIDER } from "constants/vcsProviders";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import TableSimpleCell from "ds/components/TableSimple/Cell";
import { TableSimpleCellProps } from "ds/components/TableSimple/types";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageVCS } from "hooks/useAnalytics/pages/vcs";
import useTypedContext from "hooks/useTypedContext";
import useCanManageVCSIntegration from "shared/VCS/access/useCanManageVCSIntegration";
import { VcsIntegration } from "types/generated";
import { showVCSDeleteIntegrationModal } from "views/Account/Integrations/registry/shared/VcsShared/components/DeleteIntegrationModal";
import { showVCSIntegrationDetailsDrawer } from "views/Account/Integrations/registry/shared/VcsShared/components/Details";
import {
  getEditIntegrationURL,
  isTestIntegrationConnectionAvailable,
} from "views/Account/Integrations/registry/shared/VcsShared/helpers";
import useDeleteVcsIntegration from "views/Account/Integrations/registry/shared/VcsShared/useDeleteVcsIntegration";
import useTestVCSIntegrationConnection from "views/Account/Integrations/registry/shared/VcsShared/useTestVCSIntegrationConnection";

import { VCSIntegrationsListContext } from "../../Context";
import { getVCSIntegrationId } from "../../helpers";

const VCSIntegrationsTableDropdownCell = ({
  item: integration,
  ...cellProps
}: TableSimpleCellProps<VcsIntegration>) => {
  const navigate = useNavigate();
  const trackSegmentAnalyticsEvent = useAnalytics({ page: AnalyticsPageVCS.VCSIntegrations });
  const { refetchDefaultIntegrations, refetchIntegrations } = useTypedContext(
    VCSIntegrationsListContext
  );

  const canManageVCSIntegrations = useCanManageVCSIntegration()(integration);
  const isTestConnectionAvailable = isTestIntegrationConnectionAvailable(integration.details);

  const { deleteVcsIntegration } = useDeleteVcsIntegration();
  const { testIntegrationConnection, loading: isTestingConnection } =
    useTestVCSIntegrationConnection({
      id: integration.id,
      name: integration.name || `Default ${HUMANIZE_PROVIDER[integration.provider]}`,
      provider: integration.provider,
    });

  const totalUsedByEntitiesQty = integration.searchUsersSuggestions?.filteredCount || 0;
  const usedByPath = `./${integration.id}`;

  const handleShowDetails = useCallback(() => {
    showVCSIntegrationDetailsDrawer({
      id: getVCSIntegrationId(integration),
      item: integration,
      canEdit: canManageVCSIntegrations,
    });
  }, [canManageVCSIntegrations, integration]);

  const handleEdit = useCallback(() => {
    if (integration.id) {
      navigate(getEditIntegrationURL(integration.provider, integration.id));
    }
  }, [integration.id, integration.provider, navigate]);

  const handleDelete = useCallback(() => {
    if (integration.id) {
      deleteVcsIntegration(integration.id, integration.provider, totalUsedByEntitiesQty, () => {
        refetchDefaultIntegrations();
        refetchIntegrations();
      });
    }
  }, [
    deleteVcsIntegration,
    integration.id,
    integration.provider,
    refetchDefaultIntegrations,
    refetchIntegrations,
    totalUsedByEntitiesQty,
  ]);

  const handleDeleteConfirmationShow = useCallback(() => {
    showVCSDeleteIntegrationModal({
      attachedEntitiesQty: totalUsedByEntitiesQty,
      integrationName: integration.name,
      linkPath: usedByPath,
      onConfirm: handleDelete,
    });

    trackSegmentAnalyticsEvent("Integration - Unlink Start");
  }, [
    handleDelete,
    integration.name,
    totalUsedByEntitiesQty,
    trackSegmentAnalyticsEvent,
    usedByPath,
  ]);

  return (
    <TableSimpleCell {...cellProps}>
      <DropdownMenuEllipsis tooltip="Integration actions">
        {integration.id && <CopyFieldDropdownMenuItem title="Copy ID" value={integration.id} />}

        <DropdownMenuItem onAction={handleShowDetails}>See details</DropdownMenuItem>

        {canManageVCSIntegrations && (
          <>
            {isTestConnectionAvailable && (
              <DropdownMenuItem onAction={testIntegrationConnection} loading={isTestingConnection}>
                Test
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onAction={handleEdit}>Edit</DropdownMenuItem>
            <DropdownMenuItem onAction={handleDeleteConfirmationShow} danger>
              Delete
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuEllipsis>
    </TableSimpleCell>
  );
};

export default VCSIntegrationsTableDropdownCell;
